import React, {useEffect, useRef, useState, useCallback} from 'react'

import {
  Wrapper,
  Dropdown,
  LoadingState,
  EmptyState,
  DropdownItem,
  ItemContent,
  ItemTitle,
  Spinner,
} from './AsyncSearchField.style'

import {Input} from '../../../../shared/Input'
import {useAuthClient} from '../../../../../utils/use-auth-client'
import {StyledSearchIcon} from '../../../../shared/icons/icons.styles'

// XP-Code autocomplete for the "Is this sale on behalf of another Sales or Partner?"
// toggle. Suggestions come from two different sources depending on which partner type
// is selected — reseller_accounts (Legacy Partner) or keap_partner_links (Keap Partner) —
// but the field itself always stores a plain string (xpCode), matching every downstream
// consumer (order-service, account-information.js, etc). Unlike AsyncSearchField's
// "Search by Name" (which requires an explicit Add step), this behaves like a normal
// text input: every keystroke commits the typed value, and clicking a suggestion
// immediately commits that value too.
function xpCodeSearchType(salesRepOrPartner) {
  return salesRepOrPartner === 'keap-partner'
    ? 'keap-partner'
    : 'legacy-partner'
}

export function XpCodeSearchField({
  field,
  value,
  values,
  variant,
  size,
  disabled,
  error,

  onBlur = () => {},
  onChange,
}) {
  const client = useAuthClient()

  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const wrapperRef = useRef(null)
  const query = value || ''

  const searchType = xpCodeSearchType(values?.salesRepOrPartner)

  const fetchXpCodes = useCallback(
    async inputValue => {
      const response = await client(
        `accounts/xp-code-search?type=${searchType}&q=${encodeURIComponent(
          inputValue,
        )}`,
      )
      return response?.data || []
    },
    [client, searchType],
  )

  useEffect(() => {
    if (query.length < 1) {
      setResults([])
      return
    }

    const timeout = setTimeout(async () => {
      try {
        setLoading(true)

        const data = await fetchXpCodes(query)

        setResults(data)
      } catch (error) {
        console.error(error)

        setResults([])
      } finally {
        setLoading(false)
      }
    }, 400)

    return () => clearTimeout(timeout)
  }, [query, fetchXpCodes])

  useEffect(() => {
    const handleOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)

        onBlur?.(field.key)
      }
    }

    document.addEventListener('mousedown', handleOutside)

    return () => {
      document.removeEventListener('mousedown', handleOutside)
    }
  }, [field.key, onBlur])

  return (
    <Wrapper ref={wrapperRef}>
      <Input
        label={field.label}
        placeholder={field.placeholder}
        value={query}
        variant={variant}
        size={size}
        disabled={disabled}
        labelLeftIcon={field.labelLeftIcon}
        leftIcon={<StyledSearchIcon />}
        infoText={field.infoText}
        infoLink={field.infoLink}
        infoLinkLabel={field.infoLinkLabel}
        helperText={field.helperText}
        errorText={error}
        required={field.validators?.includes('required')}
        onFocus={() => setOpen(true)}
        onChange={e => {
          onChange(field.key, e.target.value)
          setOpen(true)
        }}
        onBlur={() => onBlur(field.key)}
      />

      {open && query.length >= 1 && (
        <Dropdown>
          {loading && (
            <LoadingState>
              <Spinner />
              Searching...
            </LoadingState>
          )}

          {!loading &&
            results.map(code => (
              <DropdownItem
                key={code}
                type="button"
                onClick={() => {
                  onChange(field.key, code)
                  setOpen(false)
                }}
              >
                <ItemContent>
                  <ItemTitle>{code}</ItemTitle>
                </ItemContent>
              </DropdownItem>
            ))}

          {!loading && results.length === 0 && (
            <EmptyState>No results found</EmptyState>
          )}
        </Dropdown>
      )}
    </Wrapper>
  )
}
